import { useEffect, useState, type FormEvent } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from '@/components/common/AppModal';
import Icon from './Icon';
import type { NewNodePayload, NodePatch } from '../utils/treeMutations';
import type { SelectedClusterNode } from '../types';
import { decodeName } from '../utils/decodeName';

export type NodeEditorMode = 'add' | 'edit';

type ClusterNodeEditorModalProps = {
  show: boolean;
  mode: NodeEditorMode;
  parentLabel?: string;
  /** Pre-fill when editing. */
  node?: SelectedClusterNode | null;
  busy?: boolean;
  onHide: () => void;
  onSubmitAdd: (payload: NewNodePayload) => void | Promise<void>;
  onSubmitEdit: (patch: NodePatch) => void | Promise<void>;
};

export default function ClusterNodeEditorModal({
  show,
  mode,
  parentLabel,
  node,
  busy = false,
  onHide,
  onSubmitAdd,
  onSubmitEdit
}: ClusterNodeEditorModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setError('');
    if (mode === 'edit' && node) {
      setName(decodeName(node.rawName || node.label));
      return;
    }
    setName('');
  }, [show, mode, node]);

  const title = mode === 'add' ? 'افزودن نود' : 'ویرایش نود';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('نام نود الزامی است');
      return;
    }
    setError('');
    try {
      if (mode === 'add') {
        await onSubmitAdd({ name: trimmed });
        return;
      }
      await onSubmitEdit({ name: trimmed });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ذخیره نود');
    }
  };

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered dir="rtl">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title className="fs-0 d-flex align-items-center gap-2">
            <Icon name={mode === 'add' ? 'plus-lg' : 'pencil'} />
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mode === 'add' && parentLabel ? (
            <p className="fs--1 text-600 mb-3">
              زیرشاخه زیر{' '}
              <span className="fw-semi-bold text-body">{parentLabel}</span>
            </p>
          ) : null}

          <Form.Group className="mb-0" controlId="cluster-node-name">
            <Form.Label className="fs--1">نام نود</Form.Label>
            <Form.Control
              size="sm"
              value={name}
              disabled={busy}
              autoFocus
              onChange={e => setName(e.target.value)}
              placeholder="مثلاً خرید گوشی"
            />
          </Form.Group>

          {error ? (
            <div className="text-danger fs--1 mt-3 mb-0">{error}</div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="falcon-default"
            size="sm"
            disabled={busy}
            onClick={onHide}
          >
            انصراف
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={busy}>
            {busy ? 'در حال ذخیره…' : mode === 'add' ? 'افزودن' : 'ذخیره'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
